import { useMemo, useRef, useState, useTransition } from "react"
import { debounce } from "es-toolkit/function"
import { fetchQuery, graphql } from "relay-runtime"
import { useIcdDiagnosisQuery } from "@/modules/patients/hooks/__generated__/useIcdDiagnosisQuery.graphql"
import { RelayEnvironment } from "@/relay-environment"
import { Gender, IcdCodesSearchType } from "@/types/__generated__/union-types"

export const icdDiagnosisQuery = graphql`
  query useIcdDiagnosisQuery(
    $searchText: String!
    $searchType: IcdCodesSearchType!
    $excludedIcdCodes: [String!]!
    $birthDay: Date
    $gender: Gender
  ) {
    icdDiagnosisSearch(
      input: {
        searchText: $searchText
        searchType: $searchType
        excludedIcdCodes: $excludedIcdCodes
        birthDay: $birthDay
        gender: $gender
        top: 50
      }
    ) {
      commons {
        highlights {
          key
          value
        }
        entry {
          id
          diagnosisCode
          text
          primaryCode
          ...IcdDiagnosisFragment
        }
      }
      others {
        highlights {
          key
          value
        }
        entry {
          id
          diagnosisCode
          text
          primaryCode
          ...IcdDiagnosisFragment
        }
      }
    }
  }
`

const getSearchText = (searchValue: string) => {
  if (searchValue.length < 3) return ""

  const [icdCode] = searchValue.trim().split(" - ")
  return icdCode
}

type FetchIcdCodeParams = {
  searchType: IcdCodesSearchType
  birthDay?: string
  gender?: Gender
  excludedIcdCodes?: string[]
}

export const useFetchIcdCodesQuery = ({ birthDay, excludedIcdCodes, gender, searchType }: FetchIcdCodeParams) => {
  const searchTextRef = useRef("")
  const fetchVariables = useMemo(
    () => ({
      searchType,
      excludedIcdCodes: searchType === "EPISODE_OF_CARE" && excludedIcdCodes ? excludedIcdCodes : [],
      birthDay,
      gender,
    }),
    [birthDay, excludedIcdCodes, gender, searchType],
  )
  const [data, setData] = useState<useIcdDiagnosisQuery["response"] | null>(null)
  const [isLoading, startTransition] = useTransition()

  const debounceSearch = useMemo(
    () =>
      debounce((searchValue: string) => {
        const newSearchText = getSearchText(searchValue)
        if (newSearchText !== searchTextRef.current) {
          searchTextRef.current = newSearchText

          startTransition(() => {
            return new Promise((resolve) => {
              fetchQuery<useIcdDiagnosisQuery>(
                RelayEnvironment,
                icdDiagnosisQuery,
                { ...fetchVariables, searchText: searchTextRef.current },
                { fetchPolicy: "network-only" },
              ).subscribe({
                next: (data) => {
                  setData(data)
                  resolve()
                },
              })
            })
          })
        }
      }, 500),
    [fetchVariables],
  )

  return {
    icdCodes: data?.icdDiagnosisSearch,
    debounceSearch,
    isLoading,
  }
}
